import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface DlpRow {
  readonly policyId: string;
  readonly policyCode: string;
  readonly policyName: string;
  readonly dataClass: string;
  readonly severity: string;
  readonly enabled: boolean;
}

const CLASSES = ['', 'PII', 'PCI', 'PHI', 'FINANCIAL', 'CREDENTIAL', 'IP', 'CONFIDENTIAL', 'CUSTOM'];

/**
 * GR L30.11 — Data Loss Prevention (`/security/dlp`). Catalogue of the GR
 * net-new `dlp_policy` rules: what sensitive data class each protects, its
 * severity and whether it is enabled. Filterable by data class.
 */
export function GrDlpPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<DlpRow[]>([]);
  const [dataClass, setDataClass] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const qs = dataClass ? `?dataClass=${dataClass}` : '';
    apiRequest<{ data: DlpRow[] }>(`/v1/security/dlp/policies${qs}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, dataClass]);

  return (
    <main aria-label="security-dlp">
      <h1>{t('gr30.dlp.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.dlp.labelClass')}
          <select aria-label="filter-class" value={dataClass} onChange={(e) => setDataClass(e.target.value)}>
            {CLASSES.map((c) => <option key={c || 'all'} value={c}>{c || t('gr30.dlp.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.dlp.loading')}</p> : (
        <>
          <p aria-label="dlp-count">{t('gr30.dlp.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.dlp.empty')}</p> : (
            <table aria-label="dlp-table">
              <thead><tr><th>{t('gr30.dlp.colCode')}</th><th>{t('gr30.dlp.colName')}</th><th>{t('gr30.dlp.colClass')}</th><th>{t('gr30.dlp.colSeverity')}</th><th>{t('gr30.dlp.colEnabled')}</th></tr></thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={d.policyId}>
                    <td>{d.policyCode}</td><td>{d.policyName}</td><td>{d.dataClass}</td>
                    <td>{d.severity}</td><td>{d.enabled ? t('gr30.dlp.yes') : t('gr30.dlp.no')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
